import { createSlice } from "@reduxjs/toolkit";
import {
  send_message_thunk,
  get_initial_messages_thunk,
  get_older_messages_thunk,
  get_newer_messages_thunk,
  mark_messages_as_read_thunk,
  get_unread_message_count_thunk,
  upload_image_message_thunk,
  upload_audio_message_thunk,
  update_message_thunk
} from "./chatThunk";

const initialState = {
  messages: [],
  pagination: {
    total: 0,
    limit: 20,
    hasMore: false,
    cursors: {
      before: null,
      after: null
    }
  },
  unreadCounts: [],
  loading: false,
  loadingOlder: false,
  loadingNewer: false,
  error: null,
  messageEditing: null
};

// Send message reducer cases
const sendMessageCases = (builder) => {
  builder
    .addCase(send_message_thunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(send_message_thunk.fulfilled, (state, action) => {
      state.loading = false;
      
      // First, remove any temporary/pending messages that match this one
      state.messages = state.messages.filter(msg => {
        // Remove temporary messages with matching content
        return !msg.pending || 
               (msg.sender !== action.payload.data.sender || 
                msg.message !== action.payload.data.message);
      });
      
      // Then add the permanent message from the API
      state.messages.push(action.payload.data);
      
      // Update the after cursor to the new message
      if (state.pagination.cursors) {
        state.pagination.cursors.after = action.payload.data._id;
      }
    })
    .addCase(send_message_thunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
};

// Get initial messages reducer cases
const getInitialMessagesCases = (builder) => {
  builder
    .addCase(get_initial_messages_thunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(get_initial_messages_thunk.fulfilled, (state, action) => {
      state.loading = false;
      // Ensure initial messages are in chronological order (oldest to newest)
      state.messages = action.payload.data.messages;
      
      // For initial messages, we set hasMore based on the received messages count
      // Only if we received exactly the requested limit might there be more messages
      const hasMoreMessages = action.payload.data.messages.length >= action.payload.data.pagination.limit;
      
      state.pagination = {
        ...action.payload.data.pagination,
        hasMore: action.payload.data.pagination.hasMore && hasMoreMessages
      };
    })
    .addCase(get_initial_messages_thunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
};

// Get older messages reducer cases
const getOlderMessagesCases = (builder) => {
  builder
    .addCase(get_older_messages_thunk.pending, (state) => {
      state.loadingOlder = true;
      state.error = null;
    })
    .addCase(get_older_messages_thunk.fulfilled, (state, action) => {
      state.loadingOlder = false;
      // Prepend older messages to the beginning of the array
      // The API returns messages in chronological order (oldest to newest)
      state.messages = [...action.payload.data.messages, ...state.messages];
      
      // Update pagination info
      state.pagination = {
        ...state.pagination,
        total: action.payload.data.pagination.total,
        hasMore: action.payload.data.pagination.hasMore,
        cursors: {
          ...state.pagination.cursors,
          before: action.payload.data.pagination.cursors.before
        }
      };
    })
    .addCase(get_older_messages_thunk.rejected, (state, action) => {
      state.loadingOlder = false;
      state.error = action.payload;
    });
};

// Get newer messages reducer cases
const getNewerMessagesCases = (builder) => {
  builder
    .addCase(get_newer_messages_thunk.pending, (state) => {
      state.loadingNewer = true;
      state.error = null;
    })
    .addCase(get_newer_messages_thunk.fulfilled, (state, action) => {
      state.loadingNewer = false;
      // Append newer messages to the end of the array
      state.messages = [...state.messages, ...action.payload.data.messages];
      
      // Update pagination info
      state.pagination = {
        ...state.pagination,
        total: action.payload.data.pagination.total,
        hasMore: action.payload.data.pagination.hasMore,
        cursors: {
          ...state.pagination.cursors,
          after: action.payload.data.pagination.cursors.after
        }
      };
    })
    .addCase(get_newer_messages_thunk.rejected, (state, action) => {
      state.loadingNewer = false;
      state.error = action.payload;
    });
};

// Mark messages as read reducer cases
const markMessagesAsReadCases = (builder) => {
  builder
    .addCase(mark_messages_as_read_thunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(mark_messages_as_read_thunk.fulfilled, (state, action) => {
      state.loading = false;
      // Update unread counts for the room
      const { roomId } = action.payload;
      state.unreadCounts = state.unreadCounts.map((item) =>
        item._id === roomId ? { ...item, count: 0 } : item
      );
    })
    .addCase(mark_messages_as_read_thunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
};

// Get unread message count reducer cases
const getUnreadMessageCountCases = (builder) => {
  builder
    .addCase(get_unread_message_count_thunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(get_unread_message_count_thunk.fulfilled, (state, action) => {
      state.loading = false;
      state.unreadCounts = action.payload.data;
    })
    .addCase(get_unread_message_count_thunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
};

// Upload image reducer cases
const uploadImageCases = (builder) => {
  builder
    .addCase(upload_image_message_thunk.pending, (state) => {
      state.error = null;
      // Note: We don't set loading: true here because we already show the loading state
      // through the temp message with uploading: true flag
    })
    .addCase(upload_image_message_thunk.fulfilled, (state, action) => {
      // Get the uploaded message from the response
      const uploadedMessage = action.payload.data;
      
      // Remove any temporary uploading messages
      state.messages = state.messages.filter(msg => {
        // Keep all messages that aren't uploading, or have a different sender/content
        return !msg.uploading || 
              (msg.sender !== uploadedMessage.sender || 
               msg.messageType !== uploadedMessage.messageType);
      });
      
      // Add the confirmed message from server
      state.messages.push(uploadedMessage);
      
      // Update the after cursor to the new message
      if (state.pagination.cursors) {
        state.pagination.cursors.after = uploadedMessage._id;
      }
    })
    .addCase(upload_image_message_thunk.rejected, (state, action) => {
      state.error = action.payload;
      // Find the uploading message and mark it as failed
      state.messages = state.messages.map(msg => {
        if (msg.uploading) {
          return {
            ...msg, 
            uploading: false,
            failed: true
          };
        }
        return msg;
      });
    });
};

// Upload audio reducer cases
const uploadAudioCases = (builder) => {
  builder
    .addCase(upload_audio_message_thunk.pending, (state) => {
      state.error = null;
      // Note: We don't set loading: true here because we already show the loading state
      // through the temp message with uploading: true flag
    })
    .addCase(upload_audio_message_thunk.fulfilled, (state, action) => {
      // Get the uploaded message from the response
      const uploadedMessage = action.payload.data;
      
      // Remove any temporary uploading messages
      state.messages = state.messages.filter(msg => {
        // Keep all messages that aren't uploading, or have a different sender/content
        return !msg.uploading || 
              (msg.sender !== uploadedMessage.sender || 
               msg.messageType !== uploadedMessage.messageType);
      });
      
      // Add the confirmed message from server
      state.messages.push(uploadedMessage);
      
      // Update the after cursor to the new message
      if (state.pagination.cursors) {
        state.pagination.cursors.after = uploadedMessage._id;
      }
    })
    .addCase(upload_audio_message_thunk.rejected, (state, action) => {
      state.error = action.payload;
      // Find the uploading message and mark it as failed
      state.messages = state.messages.map(msg => {
        if (msg.uploading && msg.messageType === 'audio') {
          return {
            ...msg, 
            uploading: false,
            failed: true
          };
        }
        return msg;
      });
    });
};

// Update message reducer cases
const updateMessageCases = (builder) => {
  builder
    .addCase(update_message_thunk.pending, (state) => {
      state.error = null;
      // Note: We don't set loading: true here because the message
      // is already updated optimistically in the action
    })
    .addCase(update_message_thunk.fulfilled, (state, action) => {
      // Get the updated message from the response
      const updatedMessage = action.payload.data;
      
      // Replace the message in the state
      state.messages = state.messages.map(msg => 
        msg._id === updatedMessage._id ? updatedMessage : msg
      );
      
      // Clear messageEditing state
      state.messageEditing = null;
    })
    .addCase(update_message_thunk.rejected, (state, action) => {
      state.error = action.payload;
      // Could revert the message to original state here if needed
      state.messageEditing = null;
    });
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    addMessage: (state, action) => {
      // Add a new message to the state
      const newMessage = action.payload;
      
      // If this is an image message with a fileUrl, ensure it's not marked as uploading
      if (newMessage.messageType === 'image' && newMessage.fileUrl) {
        newMessage.uploading = false;
      }
      
      // Ensure message has a sequence number
      if (!newMessage.sequence) {
        newMessage.sequence = newMessage.timestamp ? 
          new Date(newMessage.timestamp).getTime() : 
          Date.now();
      }
      
      // First, check if this is a permanent message that should replace a temporary one
      if (newMessage._id && !newMessage._id.startsWith('temp-')) {
        // Remove any temporary/pending messages with the same content
        state.messages = state.messages.filter(msg => {
          // Keep the message if it's not a pending message OR if it has different content
          return !msg.pending || 
                 (msg.sender !== newMessage.sender || 
                  msg.message !== newMessage.message);
        });
      }
      
      // Then check if message already exists by ID or similar content to avoid duplicates
      const messageExists = 
        // Check by ID if we have one
        (newMessage._id && state.messages.some(msg => msg._id === newMessage._id)) ||
        // Or check by content matching (sender + message text + similar createdAt within 5 seconds)
        state.messages.some(msg => 
          msg.sender === newMessage.sender && 
          msg.message === newMessage.message &&
          // If both have createdAt, check if they're close (within 5 seconds)
          (msg.createdAt && newMessage.createdAt ? 
            Math.abs(new Date(msg.createdAt).getTime() - new Date(newMessage.createdAt).getTime()) < 5000 
            : false)
        );
      
      if (!messageExists) {
        // Add the new message
        state.messages.push(newMessage);
        
        // Sort messages by sequence number (oldest first, newest last)
        state.messages.sort((a, b) => {
          const seqA = a.sequence || (a.createdAt ? new Date(a.createdAt).getTime() : Date.now());
          const seqB = b.sequence || (b.createdAt ? new Date(b.createdAt).getTime() : Date.now());
          return seqA - seqB;
        });
        
        // Update the after cursor to the new message if it's a permanent message
        if (state.pagination && state.pagination.cursors && newMessage._id && !newMessage._id.startsWith('temp-')) {
          state.pagination.cursors.after = newMessage._id;
        }
      }
    },
    clearMessages: (state) => {
      state.messages = [];
      state.pagination = {
        total: 0,
        limit: 20,
        hasMore: false,
        cursors: {
          before: null,
          after: null
        }
      };
    },
    updatePagination: (state, action) => {
      // Update specific pagination properties without overwriting the entire object
      state.pagination = {
        ...state.pagination,
        ...action.payload
      };
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Add new reducers for message editing
    setMessageEditing: (state, action) => {
      state.messageEditing = action.payload;
    },
    updateMessage: (state, action) => {
      const { messageId, message, isEditing, isEdited, editedAt, error, preventScroll } = action.payload;
      
    
      // First try to find the message by exact ID
      let foundMessage = state.messages.find(msg => msg._id === messageId);
      let matchedBy = foundMessage ? 'exact_id' : null;
      
      // If not found, try a series of alternative matching strategies
      if (!foundMessage && typeof messageId === 'string') {
        // 1. Try to match by ObjectID part (before any dashes)
        const baseId = messageId.split('-')[0];
        foundMessage = state.messages.find(msg => 
          msg._id.includes(baseId) || 
          (typeof msg._id === 'string' && baseId.includes(msg._id.split('-')[0]))
        );
        if (foundMessage) matchedBy = 'partial_id';
        
        // 2. If still not found, try matching by message content
        if (!foundMessage && message) {
          foundMessage = state.messages.find(msg => 
            msg.message === message || 
            (message.length > 10 && msg.message && msg.message.includes(message.substring(0, 10)))
          );
          if (foundMessage) matchedBy = 'content';
        }
        
        // 3. Try matching by timestamp proximity with message content similarity
        if (!foundMessage && editedAt) {
          const editTime = new Date(editedAt).getTime();
          // Find messages within 1 minute of the edit time
          const possibleMatches = state.messages.filter(msg => {
            if (!msg.createdAt) return false;
            const msgTime = new Date(msg.createdAt).getTime();
            return Math.abs(msgTime - editTime) < 60000; // Within 1 minute
          });
          
          // If we have possible matches, try to find one with similar content
          if (possibleMatches.length && message) {
            foundMessage = possibleMatches.find(msg => {
              if (!msg.message || typeof msg.message !== 'string') return false;
              if (message.length < 5) return msg.message === message;
              // For longer messages, check if there's overlap
              return msg.message.includes(message.substring(0, 5)) || 
                     message.includes(msg.message.substring(0, 5));
            });
            if (foundMessage) matchedBy = 'time_and_content';
          }
        }
      }
      
    
      
      // Perform the update on all matching messages
      let updated = false;
      state.messages = state.messages.map(msg => {
        // Update if ID matches exactly OR we found a matching message by other means
        const isMatch = msg._id === messageId || 
                        (foundMessage && msg._id === foundMessage._id);
        
        if (isMatch) {
          updated = true;
          // Merge all properties from the payload with the existing message
          return {
            ...msg,
            // Only update fields that are provided in the payload
            message: message !== undefined ? message : msg.message,
            isEditing: isEditing !== undefined ? isEditing : msg.isEditing,
            isEdited: isEdited !== undefined ? isEdited : msg.isEdited,
            editedAt: editedAt !== undefined ? editedAt : msg.editedAt,
            error: error !== undefined ? error : msg.error,
            preventScroll: preventScroll !== undefined ? preventScroll : msg.preventScroll
          };
        }
        return msg;
      });
      
      if (!updated && foundMessage) {
        console.error("Logic error: Found message but didn't update it", {
          messageId,
          foundMessageId: foundMessage._id
        });
      }
    }
  },
  extraReducers: (builder) => {
    sendMessageCases(builder);
    getInitialMessagesCases(builder);
    getOlderMessagesCases(builder);
    getNewerMessagesCases(builder);
    markMessagesAsReadCases(builder);
    getUnreadMessageCountCases(builder);
    uploadImageCases(builder);
    uploadAudioCases(builder);
    updateMessageCases(builder);
  },
});

export const {
  addMessage,
  clearMessages,
  updatePagination,
  setLoading,
  setError,
  setMessageEditing,
  updateMessage
} = chatSlice.actions;

export default chatSlice.reducer; 